/**
 * Licensed Materials - Property of IBM
 * (c) Copyright IBM Corporation 2017, 2024. All Rights Reserved.
 *
 * Note to U.S. Government Users Restricted Rights:
 * Use, duplication or disclosure restricted by GSA ADP Schedule
 * Contract with IBM Corp.
 */
/*global module require*/
"use strict";

function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _classCallCheck(a, n) { if (!(a instanceof n)) throw new TypeError("Cannot call a class as a function"); }
function _defineProperties(e, r) { for (var t = 0; t < r.length; t++) { var o = r[t]; o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, _toPropertyKey(o.key), o); } }
function _createClass(e, r, t) { return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", { writable: !1 }), e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
var analytics = require("../../../lib/shared/analytics"),
  capabilitiesModel = require("./capability"),
  config = require("../../../lib/shared/config"),
  Log = require("../../../lib/client/setup/log"),
  messages = require("../../../nls/setup/deploy/deploy.properties"),
  URITemplate = require("../../../lib/shared/uritemplate"),
  xhrPromise = require("../../../lib/client/xhr-promise");
Object.assign = Object.assign || require("object-assign");
var buildCapabilitiesModel = capabilitiesModel.buildCapabilitiesModel;
var zipTemplate = new URITemplate("{+context}/setup/api/v2/template-ui/zip/{zip_url}");
var zipTemplateWithoutLoggedIn = new URITemplate("{+context}/setup/api/v2/template/zip/{zip_url}{?auth}");
var logger = Log.getLogger("model");

/**
 * Performs the ajax call to fetch template for a repository or app and build the
 * template/capability model.
 */
module.exports = /*#__PURE__*/function () {
  function TemplateLoader(options) {
    _classCallCheck(this, TemplateLoader);
    this.loggedIn = options.loggedIn;

    // Template for repo
    this.repositoryUrl = options.repositoryUrl;
    this.branch = options.branch;
    this.type = options.type;

    // Access Token needed for private repos
    this.repositoryToken = options.repositoryToken;

    // Account id
    this.accountGuid = options.accountGuid;
    this.marketingTags = options.marketingTags;
    if (options.enableYmlPreprocessor === true) {
      this.enableYmlPreprocessor = true;
      this.source_provider = options.source_provider;
      this.app_repo = options.app_repo;
      this.params = options.params;
    } else {
      this.enableYmlPreprocessor = false;
    }

    //Template for Zip
    this.zipUrl = options.zipUrl;

    // Template redirect URI
    this.redirectUri = options.redirectUri;
    if (!this.repositoryUrl && !this.zipUrl) {
      throw new Error("Repo or zip url must be provided");
    }
    this.catalogMap = {};
    this.refreshServices = false;
    this.requestPromise = null;
    this.allowWizard = options.allowWizard;
  }

  // @param templateResultArray optional
  return _createClass(TemplateLoader, [{
    key: "analyticsProps",
    value: function analyticsProps(templateResultArray) {
      var props = {
        repo: this.repositoryUrl,
        branch: this.branch,
        type: this.type
      };
      if (this.marketingTags && Object.keys(this.marketingTags).length > 0) {
        var mtags = this.marketingTags;
        for (var mtag in mtags) {
          props[mtag] = mtags[mtag];
        }
      }
      if (templateResultArray) {
        props.source = templateResultArray[1]; // tracks where template originated from
      }
      return props;
    }
  }, {
    key: "errProps",
    value: function errProps(err) {
      if (!err) {
        return err;
      }
      var whitelist = {
        code: 1,
        description: 1,
        message: 1,
        name: 1,
        url: 1,
        branch: 1,
        guid: 1
      };
      return Object.keys(err).filter(function (k) {
        return whitelist[k];
      }).reduce(function (obj, key) {
        obj[key] = err[key];
        return obj;
      }, {});
    }

    // @returns [template, source]
  }, {
    key: "load",
    value: function load(options) {
      var _this = this;
      return this.doLoad().then(function (templateResultArray) {
        analytics.track("load.template.ok", _this.analyticsProps(templateResultArray));
        if (options.loadEventName && options.unsupportedRegion) {
          analytics.track("load.template.unsupportedRegion", {
            "region": options.unsupportedRegion.id
          });
        }
        return templateResultArray;
      }).catch(function (serverError) {
        var error = {};
        if (serverError && serverError.description) {
          error.description = serverError.description;
        } else if (typeof serverError === "string") {
          error.description = messages.get("error.genericWithDetails", [serverError]);
        }
        if (serverError.details) {
          error.details = serverError.details;
          // note, error.details.description is passed to React dangerouslySetInnerHTML - be careful of XSS bugs
        }

        // Prettify
        if (!error.title) {
          error.title = messages.get("error.template.title");
        }
        if (!error.description) {
          error.description = messages.get("error.generic");
        }
        var props = Object.assign({
          message: error.description
        }, _this.errProps(error.details), _this.analyticsProps());
        analytics.track("load.template.error", props);
        if (options.loadEventName && options.unsupportedRegion) {
          analytics.track("load.template.unsupportedRegion", {
            "region": options.unsupportedRegion.id
          });
        }
        if (serverError.details && serverError.details.code === "NEEDS_LOGIN_FOR_GHE") {
          error.redirectToLogin = true;
        }
        throw error;
      });
    }
  }, {
    key: "doLoad",
    value: function doLoad() {
      var _this2 = this;
      var template, source, capabilityInfo;
      return this.fetchTemplate().then(this.maybeRefetch.bind(this)).then(function (response) {
        response.warnings.forEach(function (w) {
          return logger.warn(w);
        });
        template = response.template;
        if (_this2.redirectUri) {
          template.redirect_uri = _this2.redirectUri;
        }
        source = response.source;
        _this2.catalogMap = response.services.reduce(function (map, service) {
          map[service.entity.unique_id] = service;
          return map;
        }, {});
        TemplateLoader.tweakTemplate(template, _this2.catalogMap);
        return template;
      }).then(function () {
        return capabilityInfo = buildCapabilitiesModel(template, _this2.catalogMap, {
          allowWizard: _this2.allowWizard
        });
      }).then(function () {
        return [template, source, capabilityInfo, _this2.catalogMap];
      });
    }
  }, {
    key: "sendRequest",
    value: function sendRequest() {
      this.requestPromise = xhrPromise(this.getTemplateUrl(), {
        dataType: "json"
      });
    }
  }, {
    key: "fetchTemplate",
    value: function fetchTemplate() {
      var _this3 = this;
      return this.requestPromise.then(function (result) {
        var body = result.body;
        if (!_this3.loggedIn) {
          // Add an empty services catalog
          body.services = [];
        }
        return body;
      }).catch(function (error) {
        if (error.body) {
          throw error.body;
        }
        if (error.xhr && error.xhr.status === 0) {
          error.description = "Network error";
          error.details = {
            code: "NETWORK_ERROR"
          };
        }
        throw error;
      });
    }
  }, {
    key: "getTemplateUrl",
    value: function getTemplateUrl() {
      var _this4 = this;
      var zipUrl = this.zipUrl;
      if (zipUrl) {
        if (!this.loggedIn) {
          return zipTemplateWithoutLoggedIn.expand({
            context: config.CONTEXT_PATH,
            zip_url: this.zipUrl,
            auth: "false"
          });
        }
        return zipTemplate.expand({
          context: config.CONTEXT_PATH,
          zip_url: this.zipUrl
        });
      }

      // Create toolchain from repo
      var args = {
        context: config.CONTEXT_PATH,
        repo_url: this.repositoryUrl,
        branch: this.branch || null,
        type: this.type || null,
        repository: this.repositoryUrl,
        repository_token: this.repositoryToken || null
      };
      var repoTemplate = null;
      var repoTemplatePartial = null;
      var repoTemplate_partial = "{+context}/setup/api/v2/template/repo/{repo_url}{?branch,type,repository,repository_token,auth";
      var repoTemplate_standard = "{+context}/setup/api/v2/template-ui/repo/{repo_url}{?branch,type,repository,repository_token,refreshServices";
      var repoTemplate_byoc = ",source_provider,app_repo";

      // mix in the dynamic byoc params for the yml preprocessor...
      if (this.enableYmlPreprocessor === true) {
        args.source_provider = this.source_provider;
        args.app_repo = this.app_repo;
        // inject additional params into the args
        // and also pad out the uritemplate with the
        // additional param names...
        Object.keys(this.params).forEach(function (param) {
          if (args[param] === undefined) {
            args[param] = _this4.params[param];
            repoTemplate_byoc += "," + param;
          }
        });
        if (this.loggedIn) {
          // logged in user... instantiate the full 'byoc' repo uri...
          repoTemplate = new URITemplate(repoTemplate_standard + repoTemplate_byoc + "}");
        } else {
          // non-logged in user...
          // instantiate the partial repo uri but append the 'byoc' params
          // and feature flag param if specified... this facilitates
          // feature flag control in the non-logged in case...
          repoTemplatePartial = new URITemplate(repoTemplate_partial + repoTemplate_byoc + "}");
        }
      } else {
        if (this.loggedIn) {
          // logged in user... as the dyml preprocessor is being
          // disabled for this request, append the flag...
          repoTemplate = new URITemplate(repoTemplate_standard + ",ENABLE_YML_PREPROCESSOR}");
        } else {
          // non-logged in user... same as above...
          repoTemplatePartial = new URITemplate(repoTemplate_partial + ",ENABLE_YML_PREPROCESSOR}");
        }
        // and also buffer the corresponding 'false'
        // for the flag into the args...
        args["ENABLE_YML_PREPROCESSOR"] = false;
      }
      if (this.loggedIn) {
        args.refreshServices = this.refreshServices ? "" : null;
        // still null here means it's just a 'standard' repo uri...
        if (null === repoTemplate) {
          repoTemplate = new URITemplate(repoTemplate_standard + "}");
        }
        return repoTemplate.expand(args);
      }
      // Non-logged-in user: fetch a partial template (no services catalog)
      args.auth = "false";

      // still null here means it's just a regular 'partial' repo uri...
      if (null === repoTemplatePartial) {
        repoTemplatePartial = new URITemplate(repoTemplate_partial + "}");
      }
      return repoTemplatePartial.expand(args);
    }

    // If any services are missing a schema, retry fetching the template with refreshServices true
  }, {
    key: "maybeRefetch",
    value: function maybeRefetch(response) {
      if (!this.loggedIn) {
        return response; // no services
      }
      if (this.refreshServices) {
        return response; // already fresh
      }
      var catalog = {};
      response.services.forEach(function (s) {
        return catalog[s.entity.unique_id] = s;
      });
      var template = response.template;
      var services = template.services;
      var servicesComplete = Object.keys(services).every(function (name) {
        var s = services[name];
        return s.schema || catalog[s.service_id];
      });
      var formsComplete = Object.keys(template.form).every(function (serviceId) {
        var f = template.form[serviceId];
        return f.schema || catalog[serviceId];
      });
      if (servicesComplete && formsComplete) {
        return response; // everything is fine
      }

      // Retry with a refresh
      this.refreshServices = true;
      return this.fetchTemplate();
    }

    // Mutates this.template from v2 shape into the legacy shape expected by the capabilities model,
    // widgets, etc. This involves some stitching together of data
  }], [{
    key: "tweakTemplate",
    value: function tweakTemplate(template, catalogMap) {
      var services = template.services;
      var form = template.form || {};
      Object.keys(services).forEach(function (key) {
        return stitchSchema(catalogMap, services, key);
      });
      Object.keys(form).forEach(function (key) {
        return stitchSchema(catalogMap, form, key);
      });
    }
  }]);
}();

// Attach schema to service or form
function stitchSchema(catalog, map, key) {
  var service = map[key];
  var serviceId = service.service_id || key;
  var brokerEntry = catalog[serviceId];
  if (!brokerEntry) {
    return;
  }

  // If this service/form doesn't carry its own schema, stitch in schema from broker entry
  service.service = brokerEntry;
  if (service.schema) {
    service.__schemaOveridden = true;
  } else {
    service.schema = brokerEntry.metadata.parameters;
  }
  if (!service.schema) {
    // Still no schema, provide an empty one
    service.schema = {
      $schema: "http://json-schema.org/draft-04/schema#",
      properties: {},
      required: []
    };
  }
  service.schema.properties = service.schema.properties || {};
  service.parameters = service.parameters || {};
}